/**
 * OCT28 #13 — the front office says the outside dependency is resolved (parts arrived, customer decided).
 *
 * Shown only for a parked repair whose capacity the server released (schedule_state_reason
 * 'awaiting_external_resolution'). It calls mark_interrupted_repair_ready, which moves the repair to Needs Placement;
 * the technician's resume is then the fresh placement, checked by the placement guard. The server is the authority:
 * a technician or another shop gets FORBIDDEN, and the answer is shown as it is.
 *
 * Mounted inside the open kebab menu only, so the one read happens when someone opens the menu, never per card.
 */
import { useEffect, useState } from 'react';
import { PackageCheck } from 'lucide-react';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const ANSWERS: Record<string, string> = {
  READY_FOR_PLACEMENT: 'Ready to schedule — the repair is in Needs Placement for a new placement.',
  ALREADY_READY: 'This repair was already marked ready.',
  NOT_RELEASED: 'This repair is not waiting on an outside dependency.',
  FORBIDDEN: 'Only the front office or a manager can mark this ready.',
};

export function InterruptedRepairReadyItem({ ticketId }: { ticketId: string }) {
  const { toast } = useToast();
  const [awaiting, setAwaiting] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    // schedule_state_reason is server-owned and newer than the generated types; read it as exactly that column
    void supabase.from('tickets').select('schedule_state_reason' as '*').eq('id', ticketId).maybeSingle()
      .then(({ data }) => {
        const reason = (data as { schedule_state_reason?: string | null } | null)?.schedule_state_reason;
        if (live) setAwaiting(reason === 'awaiting_external_resolution');
      });
    return () => { live = false; };
  }, [ticketId]);

  if (!awaiting) return null;
  return (
    <DropdownMenuItem
      data-testid="kebab-interrupted-ready"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const { data, error } = await supabase.rpc('mark_interrupted_repair_ready' as never, { p_ticket_id: ticketId } as never);
        setBusy(false);
        const outcome = (data as { outcome?: string } | null)?.outcome ?? '';
        if (error || outcome !== 'READY_FOR_PLACEMENT') {
          toast({ title: 'Not marked ready', description: error?.message ?? ANSWERS[outcome] ?? outcome, variant: 'destructive' });
          return;
        }
        setAwaiting(false);
        toast({ title: 'Dependency resolved', description: ANSWERS.READY_FOR_PLACEMENT });
      }}
    >
      <PackageCheck className="mr-2 h-4 w-4" />
      Dependency resolved — ready to schedule
    </DropdownMenuItem>
  );
}
